/**
 * FE#444 — the app-side half of "Schedule again": real scheduling, real media check.
 * All decisions live in `services/schedule-again.ts`; this only wires them to the store.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import type { CommonRootState } from '@common/types/common-root-state-type';
import { probeAssetUrl } from '@common/services/asset-probe';
import {
    runScheduleAgain,
    summarizeScheduleAgain,
    scheduleAgainIdempotencyKey,
    scheduleAgainSlotCount,
    type ScheduleAgainOutcome,
    type ScheduleAgainSummary,
} from '@common/services/schedule-again';
import { planVerdictFor } from '@common/services/schedule-again-plan';
import { scheduleMessageTool } from '@common/thunks/UserMessagesThunks';

export interface ScheduleAgainResult {
    outcomes: ScheduleAgainOutcome[];
    summary: ScheduleAgainSummary;
}

export const scheduleAgainThunk = createAsyncThunk<
    ScheduleAgainResult,
    {
        messages: UserMessage[];
        day: Date;
        /** One per submit of the dialog — see `scheduleAgainIdempotencyKey`. */
        nonce: string;
    },
    { state: CommonRootState }
>('scheduleAgain', async ({ messages, day, nonce }, thunkApi) => {
    // The last line of the plan gate, whatever the dialog showed: one slot per RECIPIENT (that is how
    // many documents scheduleMessageTool will create), asked of the store as it is right now. A plan
    // we cannot read yet fails closed — nothing is created against a limit we have not seen.
    const verdict = planVerdictFor(thunkApi.getState(), scheduleAgainSlotCount(messages));
    if (verdict.refused || verdict.awaiting) {
        const outcomes: ScheduleAgainOutcome[] = messages.map(m => ({ id: m._id, status: 'plan-limit' }));
        return { outcomes, summary: summarizeScheduleAgain(outcomes) };
    }

    const outcomes = await runScheduleAgain(messages, day, {
        now: new Date(),
        newKey: (sentId, chosenDay) => scheduleAgainIdempotencyKey(sentId, chosenDay, nonce),
        probeAsset: probeAssetUrl,
        schedule: async draft => {
            // A copy is not composed in the composer: keep it off the open reply box and the media preview.
            const stored = await thunkApi.dispatch(scheduleMessageTool({ ...draft, skipComposerSideEffects: true })).unwrap();
            const created = Array.isArray(stored) ? stored[0] : stored;
            return created?._id;
        },
    });
    return { outcomes, summary: summarizeScheduleAgain(outcomes) };
});
